import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserTeamGetByManagerThunk } from "tm-shared/api";

import { ROUTES } from "@/constants";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { ProgressLoader } from "@/shared/feedback";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { Meta } from "@/shared/seo";
import { SecondaryText } from "@/shared/ui/Typography";

import { ActiveUserSection } from "./components/ActiveUserSection";
import { SearchInput } from "./components/SearchInput";
import { TeamHeader } from "./components/TeamHeader";
import { TeamList } from "./components/TeamList";
import { TeamStats } from "./components/TeamStats";
import { useLoadUserSchedule } from "./hooks/useLoadUserSchedule";
import { useTeamMembers } from "./hooks/useTeamMembers";
import { StyledScrollContent } from "./Styled";

const Team = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const managerId = useAppSelector((state) => state.user.id);
    const team = useAppSelector((state) => state.manager.team);
    const offlineMode = useOfflineModeSelector();

    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const { handleLoadUserSchedule, isProcessing, queueProgress } =
        useLoadUserSchedule();

    const { activeUser, filteredMembers, teamStats, teamRoutes } =
        useTeamMembers(searchQuery);

    useEffect(() => {
        if (!managerId || offlineMode) return;

        setIsLoading(true);

        dispatch(getUserTeamGetByManagerThunk({ managerId }))
            .unwrap()
            .finally(() => {
                setIsLoading(false);
            });
    }, [dispatch, managerId, offlineMode]);

    return (
        <>
            <Meta title="Team" />

            <Header titleLoading={isLoading} />

            <MainContainer>
                <StyledScrollContent>
                    <TeamStats stats={teamStats} />

                    <TeamHeader />

                    {activeUser && (
                        <ActiveUserSection
                            routeId={teamRoutes[activeUser?.userId ?? ""]}
                            member={activeUser}
                            isProcessing={isProcessing}
                            onLoadSchedule={handleLoadUserSchedule}
                            onNavigate={() => navigate(ROUTES.WorkPlan)}
                        />
                    )}

                    <SearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />

                    {!team?.members?.length && isLoading && (
                        <SecondaryText>Завантажуємо команду…</SecondaryText>
                    )}

                    {team?.members && team.members.length > 0 && (
                        <TeamList
                            members={filteredMembers}
                            teamRoutes={teamRoutes}
                            searchQuery={searchQuery}
                            isProcessing={isProcessing}
                            onLoadSchedule={handleLoadUserSchedule}
                            onNavigate={() => navigate(ROUTES.WorkPlan)}
                        />
                    )}
                </StyledScrollContent>
            </MainContainer>

            <ProgressLoader
                open={queueProgress.isRunning}
                tasks={queueProgress.tasks}
            />
        </>
    );
};

export default Team;
